<template>
  <ValidationObserver slim v-slot="{ handleSubmit, invalid }" vid="emails" ref="observer">
    <BaseCard>
      <BaseCardHeader>
        <div class="flex justify-between items-baseline -mr-2">
          <h5 class="font-light">Emails</h5>
          <BaseIconButton
            v-if="!edit && $isAdminOrSuperAdmin"
            icon="edit"
            brand
            @click="edit = !edit"
          />
          <template v-else>
            <div class="space-x-2 justify-end">
              <BaseIconButton icon="times" silver @click="doRevert" />
              <BaseIconButton
                green
                icon="check"
                :disabled="invalid"
                @click="handleSubmit(doSubmit)"
              />
            </div>
          </template>
        </div>
      </BaseCardHeader>
      <BaseCardBody>
        <form @submit.prevent>
          <div class="-m-6">
            <SimpleList nested :entities="localAccountEmails">
              <template #header>
                <ListingHeader />
              </template>
              <template #default="{ index, entity }">
                <ListingItem
                  :index="index"
                  :entity="entity"
                  :edit="edit"
                  :updating="updating"
                  @change="updateEntity(index, $event)"
                  @destroy="removeEntity(index)"
                />
              </template>
              <template #body-after>
                <BaseListItem v-if="localAccountEmails.length === 0">
                  <div class="flex items-baseline -mx-4">
                    <div class="flex-1 px-4 text-center">Nothing to see yet!</div>
                  </div>
                </BaseListItem>
                <BaseListItem v-if="edit && !updating">
                  <div class="flex -mx-2 justify-end">
                    <BaseIconButton icon="plus" green @click="addNewEntity" />
                  </div>
                </BaseListItem>
              </template>
            </SimpleList>
          </div>
        </form>
      </BaseCardBody>
    </BaseCard>
  </ValidationObserver>
</template>

<script>
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import ListingHeader from '@/components/AccountEmail/ListingHeader.vue';
import ListingItem from '@/components/AccountEmail/ListingItem.vue';
import SimpleList from '@/components/SimpleListFactory.vue';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';
import { entityComputed } from '@/store/helper';

const defaultEntity = {
  uuid: null,
  address: '',
};

export default {
  name: 'Emails',
  mixins: [veeValidateErrorsMixin],
  components: {
    SimpleList,
    ListingItem,
    ListingHeader,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      edit: false,
      updating: false,
      localAccountEmails: [],
    };
  },
  computed: {
    ...entityComputed,
    accountEmails() {
      const { emails: emailIds = [] } = this.$currentAccount || {};
      return emailIds.map((id) => this.entityById('accountEmails', id));
    },
  },
  watch: {
    accountEmails: {
      handler(newVal) {
        this.localAccountEmails = JSON.parse(JSON.stringify(newVal));
      },
      deep: true,
    },
  },
  created() {
    this.localAccountEmails = JSON.parse(JSON.stringify(this.accountEmails));
  },
  methods: {
    doRevert() {
      this.localAccountEmails = JSON.parse(JSON.stringify(this.accountEmails));
      this.edit = false;
    },
    async doSubmit() {
      const { dispatch } = this.$store;
      const { accountId } = this;
      const accountUpdatedData = { emails: [...this.localAccountEmails] };

      this.updating = true;

      const dispatchWithErrorHandling = this.handleFormErrors(dispatch);

      await dispatchWithErrorHandling('ui/accounts/updateAccount', {
        accountId,
        accountUpdatedData,
      });

      this.updating = false;
    },
    addNewEntity() {
      this.localAccountEmails.push({ ...defaultEntity });
    },
    removeEntity(index) {
      this.localAccountEmails.splice(index, 1);
    },
    updateEntity(index, email) {
      this.localAccountEmails.splice(index, 1, email);
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.edit = false;
          })
          .catch(({ status = null, data } = {}) => {
            if (status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            } else {
              this.errorMessage = 'Something went wrong! Please try again';
            }
          });
    },
  },
};
</script>
